export type UnitMode = 'metric' | 'imperial';

const METERS_PER_FOOT = 0.3048;
const FEET_PER_METER = 1 / METERS_PER_FOOT;

const MPS_TO_KMPH = 3.6;
const MPS_TO_MPH = 2.2369362920544;

const trimFixed = (value: number, decimals: number) => {
    const fixed = value.toFixed(decimals);
    // Strip trailing zeros and an optional trailing decimal point.
    return fixed.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1').replace(/\.$/, '');
};

export class UnitSystem {
    mode: UnitMode;

    constructor(mode: UnitMode = 'imperial') {
        this.mode = mode;
    }

    // Distance

    distanceUnit(): 'm' | 'ft' {
        return this.mode === 'imperial' ? 'ft' : 'm';
    }

    distanceLabel(): string {
        return this.mode === 'imperial' ? 'feet' : 'meters';
    }

    formatDistance(meters: number): string {
        const v = this.metersToDisplay(meters);
        const unit = this.distanceUnit();
        const decimals = unit === 'ft' ? 2 : 1;
        return `${trimFixed(v, decimals)} ${unit}`;
    }

    // Speed

    speedUnit(): 'km/h' | 'mph' {
        return this.mode === 'imperial' ? 'mph' : 'km/h';
    }

    speedLabel(): string {
        return this.mode === 'imperial' ? 'mph' : 'km/h';
    }

    formatSpeed(metersPerSec: number): string {
        const unit = this.speedUnit();
        const v = unit === 'mph' ? metersPerSec * MPS_TO_MPH : metersPerSec * MPS_TO_KMPH;
        return `${trimFixed(v, 1)} ${unit}`;
    }

    // Height

    heightUnit(): 'm' | 'ft' {
        return this.distanceUnit();
    }

    formatHeight(meters: number): string {
        const v = this.metersToDisplay(meters);
        const unit = this.heightUnit();
        const decimals = unit === 'ft' ? 2 : 1;
        return `${trimFixed(v, decimals)} ${unit}`;
    }

    // Conversion helpers

    metersToDisplay(m: number): number {
        return this.mode === 'imperial' ? m * FEET_PER_METER : m;
    }

    displayToMeters(d: number): number {
        return this.mode === 'imperial' ? d * METERS_PER_FOOT : d;
    }
}
